import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Upload, FileSpreadsheet, AlertTriangle, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinance } from '@/contexts/FinanceContext';
import { parseContaAzulFile } from '../parsers/contaAzulParser';
import { parseOFXFile } from '../parsers/ofxParser';
import { isOfxResult } from '../types';
import type { ImportType, ParseResult } from '../types';

interface Props {
  importType: ImportType;
  accountId: string;
  destinationAccountId?: string;
  onUpdate: (patch: {
    importType?: ImportType;
    accountId?: string;
    destinationAccountId?: string;
    fileName?: string;
    parseResult?: ParseResult;
  }) => void;
  onNext: () => void;
}

export function UploadStep({
  importType,
  accountId,
  destinationAccountId,
  onUpdate,
  onNext,
}: Props) {
  const { accounts } = useFinance();
  const activeAccounts = accounts.filter((a) => a.isActive);
  const [parseResult, setParseResult] = useState<ParseResult | undefined>();
  const [fileName, setFileName] = useState<string | undefined>();
  const [parsing, setParsing] = useState(false);

  const handleFile = async (file: File) => {
    setParsing(true);
    try {
      const lowerName = file.name.toLowerCase();
      const result = lowerName.endsWith('.ofx')
        ? await parseOFXFile(file)
        : await parseContaAzulFile(file);
      setParseResult(result);
      setFileName(file.name);
      onUpdate({ fileName: file.name, parseResult: result });
    } finally {
      setParsing(false);
    }
  };

  const isOfx = isOfxResult(parseResult);
  const isTransfer = importType === 'transferir';
  const isCardInvoice = importType === 'fatura-cartao';
  // Filtra contas mostradas no Select de "conta principal":
  // - Para fatura de cartão: só contas tipo credit_card.
  // - Para os outros tipos: todas exceto credit_card (que só recebe via fatura).
  const accountsForMainSelect = isCardInvoice
    ? activeAccounts.filter((a) => a.type === 'credit_card')
    : activeAccounts.filter((a) => a.type !== 'credit_card');
  const canProceed =
    !!accountId &&
    !!parseResult &&
    parseResult.rows.length > 0 &&
    (!isTransfer || (!!destinationAccountId && destinationAccountId !== accountId));

  return (
    <div className="space-y-5">
      {!isOfx && (
        <div className="space-y-2">
          <Label>Tipo de importação *</Label>
          <RadioGroup
            value={importType}
            onValueChange={(v) => onUpdate({ importType: v as ImportType })}
            className="flex gap-4"
          >
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 transition-colors hover:bg-secondary has-[:checked]:border-primary has-[:checked]:bg-primary/5">
              <RadioGroupItem value="pagar" />
              <span className="text-sm font-medium">Contas a pagar</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 transition-colors hover:bg-secondary has-[:checked]:border-primary has-[:checked]:bg-primary/5">
              <RadioGroupItem value="receber" />
              <span className="text-sm font-medium">Contas a receber</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 transition-colors hover:bg-secondary has-[:checked]:border-primary has-[:checked]:bg-primary/5">
              <RadioGroupItem value="transferir" />
              <span className="text-sm font-medium">Transferências</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-white px-3 py-2 transition-colors hover:bg-secondary has-[:checked]:border-primary has-[:checked]:bg-primary/5">
              <RadioGroupItem value="fatura-cartao" />
              <span className="text-sm font-medium">Fatura de cartão</span>
            </label>
          </RadioGroup>
          {isTransfer && (
            <p className="text-xs text-muted-foreground">
              Use para importar histórico de transferências entre contas (lançamentos legados).
              Todas as linhas viram <strong>transferências</strong> da conta origem para a conta
              destino.
            </p>
          )}
          {isCardInvoice && (
            <p className="text-xs text-muted-foreground">
              Cada linha do CSV vira uma <strong>compra pendente</strong> na fatura. Após importar,
              use <strong>"Fechar fatura"</strong> no card do cartão para liquidar tudo de uma vez.
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label>
          {isCardInvoice
            ? 'Cartão de crédito *'
            : isTransfer
              ? 'Conta de origem *'
              : 'Conta bancária destino *'}
        </Label>
        <Select value={accountId} onValueChange={(v) => onUpdate({ accountId: v })}>
          <SelectTrigger>
            <SelectValue
              placeholder={
                isCardInvoice
                  ? 'Selecione o cartão de crédito'
                  : 'Selecione a conta para os lançamentos'
              }
            />
          </SelectTrigger>
          <SelectContent>
            {accountsForMainSelect.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {accountsForMainSelect.length === 0 && (
          <p className="text-xs text-destructive">
            {isCardInvoice
              ? 'Cadastre um cartão de crédito antes de importar a fatura.'
              : 'Cadastre uma conta bancária antes de importar.'}
          </p>
        )}
      </div>

      {isTransfer && (
        <div className="space-y-2">
          <Label>Conta de destino *</Label>
          <Select
            value={destinationAccountId ?? ''}
            onValueChange={(v) => onUpdate({ destinationAccountId: v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a conta destino das transferências" />
            </SelectTrigger>
            <SelectContent>
              {activeAccounts
                .filter((a) => a.id !== accountId)
                .map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {destinationAccountId && destinationAccountId === accountId && (
            <p className="text-xs text-destructive">
              Conta destino não pode ser a mesma da origem.
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label>Arquivo *</Label>
        <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-white px-6 py-8 text-center transition-colors hover:border-primary hover:bg-primary/5">
          <input
            type="file"
            accept=".csv,.xlsx,.xls,.ofx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/x-ofx"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />
          <Upload size={24} className="text-muted-foreground" />
          {fileName ? (
            <>
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <FileSpreadsheet size={14} />
                {fileName}
              </div>
              <p className="text-xs text-muted-foreground">Clique para substituir</p>
            </>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">
                {parsing ? 'Processando…' : 'Clique ou arraste o arquivo'}
              </p>
              <p className="text-xs text-muted-foreground">
                Conta Azul (.xlsx, .xls, .csv) ou extrato bancário (.ofx)
              </p>
            </>
          )}
        </label>
      </div>

      {parseResult && (
        <div className="space-y-2">
          {isOfx && parseResult.ofxMetadata && parseResult.rows.length > 0 && (
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
              <div className="flex items-center gap-2 font-medium text-primary">
                <Banknote size={14} />
                Extrato bancário detectado
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                {parseResult.ofxMetadata.bankId && (
                  <span>Banco: {parseResult.ofxMetadata.bankId}</span>
                )}
                {parseResult.ofxMetadata.accountId && (
                  <span>Conta: {parseResult.ofxMetadata.accountId}</span>
                )}
                {parseResult.ofxMetadata.startDate && parseResult.ofxMetadata.endDate && (
                  <span>
                    Período:{' '}
                    {format(parseResult.ofxMetadata.startDate, 'dd/MM/yyyy', { locale: ptBR })} →{' '}
                    {format(parseResult.ofxMetadata.endDate, 'dd/MM/yyyy', { locale: ptBR })}
                  </span>
                )}
                {parseResult.ofxMetadata.currency && (
                  <span>Moeda: {parseResult.ofxMetadata.currency}</span>
                )}
              </div>
            </div>
          )}
          {parseResult.rows.length > 0 && (
            <div className="rounded-lg border border-income/30 bg-income/5 px-3 py-2 text-sm">
              <span className="font-semibold text-income">{parseResult.rows.length}</span>{' '}
              <span className="text-foreground">linhas válidas detectadas</span>
              {!isOfx && (
                <>
                  {' • '}
                  <span className="text-muted-foreground">
                    {parseResult.uniqueCategories.length} categoria(s),{' '}
                    {parseResult.uniqueCostCenters.length} centro(s) de custo
                  </span>
                </>
              )}
              {isOfx && (
                <>
                  {' • '}
                  <span className="text-muted-foreground">
                    {parseResult.rows.filter((r) => r.type === 'income').length} entrada(s),{' '}
                    {parseResult.rows.filter((r) => r.type === 'expense').length} saída(s)
                  </span>
                </>
              )}
            </div>
          )}
          {parseResult.errors.length > 0 && (
            <div className="rounded-lg border border-warning/30 bg-warning/5 px-3 py-2">
              <div className="mb-1 flex items-center gap-2 text-sm font-medium text-warning">
                <AlertTriangle size={14} />
                {parseResult.errors.length} linha(s) com erro (serão ignoradas)
              </div>
              <ul className="max-h-20 overflow-y-auto text-xs text-muted-foreground">
                {parseResult.errors.slice(0, 5).map((e, i) => (
                  <li key={i}>
                    Linha {e.line}: {e.reason}
                  </li>
                ))}
                {parseResult.errors.length > 5 && <li>…e mais {parseResult.errors.length - 5}</li>}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={onNext} disabled={!canProceed}>
          Próximo
        </Button>
      </div>
    </div>
  );
}
